import { useState, useEffect, useCallback } from 'react'
import { ga4Service } from '@/api/ga4'
import type { GA4Config, GA4FunnelData } from '@/types/ga4'

export interface UseGA4ConfigOptions {
  storeId: string
  autoLoad?: boolean
}

export interface UseGA4ConfigReturn {
  // Configuration state
  config: GA4Config | null
  isConfigured: boolean
  isLoading: boolean
  error: string | null

  // Analytics data
  funnelData: GA4FunnelData | null
  isLoadingAnalytics: boolean
  analyticsError: string | null

  // Actions
  loadConfig: () => Promise<void>
  loadAnalytics: (windowDays?: number) => Promise<void>
  refreshAll: () => Promise<void>
  clearError: () => void
}

/**
 * Hook for managing GA4 configuration and analytics data
 */
export function useGA4Config({
  storeId,
  autoLoad = true
}: UseGA4ConfigOptions): UseGA4ConfigReturn {
  // Configuration state
  const [config, setConfig] = useState<GA4Config | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Analytics state
  const [funnelData, setFunnelData] = useState<GA4FunnelData | null>(null)
  const [isLoadingAnalytics, setIsLoadingAnalytics] = useState(false)
  const [analyticsError, setAnalyticsError] = useState<string | null>(null)

  // Computed values
  const isConfigured = config?.configured === true

  // Load configuration
  const loadConfig = useCallback(async () => {
    if (!storeId) return

    setIsLoading(true)
    setError(null)

    try {
      const response = await ga4Service.getConfig(storeId)

      if (response.success && response.config) {
        setConfig(response.config as GA4Config)
      } else {
        setConfig(null)
        if (response.message && !response.message.includes('not configured')) {
          setError(response.message)
        }
      }
    } catch (err) {
      console.error('Failed to load GA4 config:', err)
      setError('Failed to load GA4 configuration')
      setConfig(null)
    } finally {
      setIsLoading(false)
    }
  }, [storeId])

  // Load analytics data
  const loadAnalytics = useCallback(async (windowDays: number = 7) => {
    if (!storeId) return

    setIsLoadingAnalytics(true)
    setAnalyticsError(null)

    try {
      // GA4 analytics are now integrated into the layered analytics system
      // Use storeService.getStoreAnalytics() to get analytics that includes GA4 data
      const { storeService } = await import('@/api/store')
      const analytics = await storeService.getStoreAnalytics(storeId, windowDays)

      if (analytics.ga4_analytics) {
        // Convert GA4 analytics to the expected funnel data format
        setFunnelData({
          steps: {
            view_item: analytics.ga4_analytics.page_views || 0,
            add_to_cart: 0, // GA4 doesn't provide this directly in our current implementation
            begin_checkout: 0, // GA4 doesn't provide this directly in our current implementation
            purchase: analytics.ga4_analytics.purchases || 0
          },
          source: 'ga4' as const
        })
      } else {
        // GA4 is configured (property saved) but the analytics response has no
        // GA4 data yet — e.g. no traffic, or the pipeline hasn't surfaced it.
        // This is the empty/no-data state, NOT a configuration error. Surfacing
        // a "not configured" alert here contradicts the "Configurado" badge.
        setFunnelData(null)
        setAnalyticsError(null)
      }
    } catch (err) {
      console.error('Failed to load GA4 analytics:', err)
      setAnalyticsError('Failed to load analytics data')
      setFunnelData(null)
    } finally {
      setIsLoadingAnalytics(false)
    }
  }, [storeId])

  // Refresh all data
  const refreshAll = useCallback(async () => {
    await Promise.all([
      loadConfig(),
      loadAnalytics()
    ])
  }, [loadConfig, loadAnalytics])

  // Clear error
  const clearError = useCallback(() => {
    setError(null)
    setAnalyticsError(null)
  }, [])

  // Auto-load on mount or storeId change
  useEffect(() => {
    if (autoLoad && storeId) {
      loadConfig()
    }
  }, [autoLoad, storeId, loadConfig])

  // Load analytics when configuration is enabled
  useEffect(() => {
    if (isConfigured && storeId) {
      loadAnalytics()
    }
  }, [isConfigured, storeId, loadAnalytics])

  return {
    // Configuration state
    config,
    isConfigured,
    isLoading,
    error,

    // Analytics data
    funnelData,
    isLoadingAnalytics,
    analyticsError,

    // Actions
    loadConfig,
    loadAnalytics,
    refreshAll,
    clearError
  }
}

/**
 * Hook for GA4 configuration modal state
 */
export function useGA4ConfigModal() {
  const [isOpen, setIsOpen] = useState(false)

  const openModal = useCallback(() => setIsOpen(true), [])
  const closeModal = useCallback(() => setIsOpen(false), [])

  return {
    isOpen,
    openModal,
    closeModal
  }
}